import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  Drawer,
  IconButton,
  Badge,
  useMediaQuery,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import React, { useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense } from "react";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import MapIcon from "@mui/icons-material/Map";
import ViewListIcon from "@mui/icons-material/ViewList";
import { useSelector } from "react-redux";
import { Annonce, selectPublicAnnonces } from "../store/annonceSlice";
import AnnonceCard from "../components/annonces/AnnonceCard";
import Localisation from "../components/annonces/Localisation";
import Pieces from "../components/annonces/Pieces";
import Chambres from "../components/annonces/Chambres";
import Prix from "../components/annonces/Prix";
import Surface from "../components/annonces/Surface";
import TypeDeBien from "../components/annonces/TypeDeBien";
import Pagination from "../components/Pagination";
import usePagination from "../hooks/usePagination";

// Lazy load du composant MapView
const MapView = lazy(() => import("../components/annonces/MapView"));

type ViewMode = "list" | "map";

type Props = {};

// Valeurs par défaut des filtres (valeurs hautes pour tout afficher par défaut)
const DEFAULT_PRIX_RANGE = [0, 10000000];
const DEFAULT_SURFACE_RANGE = [0, 10000];

const Annonces = (props: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedAnnonceId, setSelectedAnnonceId] = useState<string>();

  const annonces = useSelector(selectPublicAnnonces);
  const [filteredAnnonce, setFilteredAnnonce] = useState<Annonce[]>([]);
  const [localisation, setLocalisation] = useState<string[]>([]);
  const [pieces, setPieces] = useState<string[]>([]);
  const [chambres, setChambres] = useState<string[]>([]);
  const [prixRange, setPrixRange] = useState<number[]>(DEFAULT_PRIX_RANGE);
  const [surface, setSurface] = useState<number[]>(DEFAULT_SURFACE_RANGE);
  const [typeDeBien, setTypeDeBien] = useState<string[]>([]);
  const wrapperRef = useRef(null);
  const displayedAnnonce = usePagination(filteredAnnonce);

  // Compte le nombre de filtres actifs
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (localisation.length > 0) count++;
    if (pieces.length > 0) count++;
    if (chambres.length > 0) count++;
    if (typeDeBien.length > 0) count++;
    if (prixRange[0] !== DEFAULT_PRIX_RANGE[0] || prixRange[1] !== DEFAULT_PRIX_RANGE[1]) count++;
    if (surface[0] !== DEFAULT_SURFACE_RANGE[0] || surface[1] !== DEFAULT_SURFACE_RANGE[1]) count++;
    return count;
  }, [localisation, pieces, chambres, typeDeBien, prixRange, surface]);

  const hasActiveFilters = activeFiltersCount > 0;

  // Réinitialise tous les filtres
  const resetFilters = useCallback(() => {
    setLocalisation([]);
    setPieces([]);
    setChambres([]);
    setTypeDeBien([]);
    setPrixRange(DEFAULT_PRIX_RANGE);
    setSurface(DEFAULT_SURFACE_RANGE);
  }, []);

  useEffect(() => {
    const localisationFilter = annonces.filter((annonce) =>
      localisation.length === 0
        ? true
        : localisation.includes(
            annonce.data.data.informations.localisation.data
          )
        ? true
        : false
    );

    const typeFilter = localisationFilter.filter((annonce) =>
      typeDeBien.length === 0
        ? true
        : typeDeBien.includes(annonce.data.data.informations.typeBien)
        ? true
        : false
    );

    const pieceFilter = typeFilter.filter((annonce) =>
      pieces.length === 0
        ? true
        : pieces.includes(
            annonce.data.data.informations.nombrePieces.data.toString()
          )
        ? true
        : false
    );

    const chambreFilter = pieceFilter.filter((annonce) =>
      chambres.length === 0
        ? true
        : annonce.data.data.informations.nombreChambres?.data
        ? chambres.includes(
            annonce.data.data.informations.nombreChambres.data.toString()
          )
        : false
    );

    const prixRangeFilter = chambreFilter.filter(
      (annonce) =>
        prixRange[0] <= annonce.data.data.informations.prix.data &&
        annonce.data.data.informations.prix.data <= prixRange[1]
    );

    const surfaceFilter = prixRangeFilter.filter(
      (annonce) =>
        surface[0] <= annonce.data.data.informations.surface.data &&
        annonce.data.data.informations.surface.data <= surface[1]
    );

    // Trier par date de modification (plus récent en premier)
    const sortedByDate = [...surfaceFilter].sort(
      (a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime()
    );

    setFilteredAnnonce(sortedByDate);
  }, [annonces, localisation, pieces, chambres, prixRange, surface, typeDeBien]);

  // Composant des filtres (réutilisé dans desktop et drawer)
  const FiltersContent = (
    <>
      <Localisation
        localisation={localisation}
        setLocalisation={setLocalisation}
      />
      <TypeDeBien typeDeBien={typeDeBien} setTypeDeBien={setTypeDeBien} />
      <Pieces pieces={pieces} setPieces={setPieces} />
      <Chambres chambres={chambres} setChambres={setChambres} />
      <Prix prixRange={prixRange} setPrixRange={setPrixRange} />
      <Surface surface={surface} setSurface={setSurface} />
    </>
  );

  return (
    <Container maxWidth="xl">
      {/* Bouton filtres mobile + Drawer */}
      {isMobile ? (
        <>
          <Box display="flex" alignItems="center" gap={2} p={2}>
            <Badge
              badgeContent={activeFiltersCount}
              color="primary"
              invisible={activeFiltersCount === 0}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setDrawerOpen(true)}
                sx={{
                  borderColor: hasActiveFilters ? "primary.main" : "grey.400",
                  color: hasActiveFilters ? "primary.main" : "grey.700",
                  py: 0.75,
                  px: 1.5,
                  fontSize: "0.875rem",
                }}
              >
                Filtrer
              </Button>
            </Badge>

            {hasActiveFilters && (
              <Button
                variant="text"
                size="small"
                startIcon={<FilterListOffIcon />}
                onClick={resetFilters}
                sx={{ color: "grey.600" }}
              >
                Effacer
              </Button>
            )}
          </Box>

          <Drawer
            anchor="bottom"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                maxHeight: "85vh",
              },
            }}
          >
            <Box p={2}>
              {/* En-tête du drawer */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" fontWeight={600}>
                  Filtrer les annonces
                </Typography>
                <IconButton
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Fermer les filtres"
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Filtres en colonne pour mobile */}
              <Box display="flex" flexDirection="column" gap={2}>
                {FiltersContent}
              </Box>

              {/* Boutons d'action */}
              <Box display="flex" gap={2} mt={3}>
                {hasActiveFilters && (
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    onClick={() => {
                      resetFilters();
                      setDrawerOpen(false);
                    }}
                    sx={{ py: 1, fontSize: "0.875rem" }}
                  >
                    Effacer les filtres
                  </Button>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  size="small"
                  onClick={() => setDrawerOpen(false)}
                  sx={{ py: 1, fontSize: "0.875rem" }}
                >
                  Voir {filteredAnnonce.length} résultat{filteredAnnonce.length > 1 ? "s" : ""}
                </Button>
              </Box>
            </Box>
          </Drawer>
        </>
      ) : (
        /* Filtres desktop */
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={1}
          p={2}
        >
          {FiltersContent}

          {/* Bouton effacer les filtres */}
          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={resetFilters}
              sx={{
                ml: "auto",
                borderColor: "grey.400",
                color: "grey.700",
                "&:hover": {
                  borderColor: "primary.main",
                  color: "primary.main",
                },
              }}
            >
              Effacer les filtres
            </Button>
          )}
        </Box>
      )}

      {/* Compteur de résultats et toggle vue */}
      <Box px={2} pb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="body2" color="text.secondary">
          {filteredAnnonce.length} bien{filteredAnnonce.length > 1 ? "s" : ""} trouvé{filteredAnnonce.length > 1 ? "s" : ""}
          {hasActiveFilters && " (avec filtres)"}
        </Typography>

        {/* Toggle Liste / Carte */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && setViewMode(newMode)}
          size="small"
          aria-label="Mode d'affichage"
        >
          <ToggleButton value="list" aria-label="Vue liste">
            <Tooltip title="Vue liste">
              <ViewListIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="map" aria-label="Vue carte">
            <Tooltip title="Vue carte">
              <MapIcon />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Vue carte */}
      {viewMode === "map" && (
        <Box sx={{ height: { xs: 400, md: 600 }, mb: 4 }}>
          <Suspense
            fallback={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
                bgcolor="grey.100"
                borderRadius={2}
              >
                <Typography color="text.secondary">
                  Chargement de la carte...
                </Typography>
              </Box>
            }
          >
            <MapView
              annonces={filteredAnnonce}
              onClose={() => setViewMode("list")}
              selectedAnnonceId={selectedAnnonceId}
              onAnnonceSelect={setSelectedAnnonceId}
            />
          </Suspense>
        </Box>
      )}

      {/* Liste des annonces ou état vide */}
      {viewMode === "list" && displayedAnnonce?.length === 0 ? (
        <Box
          textAlign="center"
          py={8}
          px={2}
        >
          <SearchOffIcon
            sx={{
              fontSize: 64,
              color: "grey.400",
              mb: 2
            }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Aucun bien ne correspond à vos critères
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Essayez d'élargir votre recherche en modifiant vos filtres
          </Typography>
          {hasActiveFilters && (
            <Button
              variant="contained"
              startIcon={<FilterListOffIcon />}
              onClick={resetFilters}
            >
              Réinitialiser les filtres
            </Button>
          )}
        </Box>
      ) : viewMode === "list" ? (
        <Grid
          ref={wrapperRef}
          container
          columns={{ lg: 3, xs: 1, sm: 2 }}
          spacing={4}
        >
          {displayedAnnonce?.map((annonce) => (
            <AnnonceCard key={annonce._id} annonce={annonce} />
          ))}
        </Grid>
      ) : null}
      {viewMode === "list" && (
        <Pagination
          data={filteredAnnonce}
          props={{
            sx: {
              "& .MuiPaginationItem-root:hover, & .MuiPaginationItem-root.Mui-selected, , & .MuiPaginationItem-root.Mui-selected:hover":
                {
                  backgroundColor: "transparent",
                  color: "primary.main",
                },
              "& .MuiPaginationItem-root": {
                fontWeight: "500",
              },
            },
            hideNextButton: true,
            hidePrevButton: true,
          }}
          wrapper={wrapperRef}
        />
      )}
    </Container>
  );
};

export default Annonces;
